import { creatorListingsPath } from "@travily/routes";
import { useState } from "react";
import { useWizardForm } from "@/lib/wizard/use-wizard-form";

type SupportedCountry = { code: string; name: string; currency: string };

interface UseSellingCountryStageArgs {
  /** Builds the URL for a given stage of this listing's creator flow. */
  stageUrl: (stage: string) => string;
  stageIndex: number;
  stageOrder: string[];
  country: string | null;
  currentCurrency: string;
  repriceableCount: number;
  supportedCountries: SupportedCountry[];
}

/**
 * Shared wiring for the selling-country wizard step (product + service). Owns the
 * form, the picked-country lookup, and the confirm-before-reprice flow: choosing a
 * country whose currency differs from the seller's current one and that would
 * re-price existing paid drafts opens a confirm modal; dismissing it reverts the
 * picker to the current country so nothing changes.
 */
export function useSellingCountryStage({
  stageUrl,
  stageIndex,
  stageOrder,
  country,
  currentCurrency,
  repriceableCount,
  supportedCountries,
}: UseSellingCountryStageArgs) {
  const wizard = useWizardForm<{ country: string }>({
    routes: { stageUrl, entryUrl: creatorListingsPath() },
    stageName: "selling_country",
    stageIndex,
    stageOrder,
    initialData: { country: country ?? "" },
  });

  const [confirmOpen, setConfirmOpen] = useState(false);
  const selected = supportedCountries.find((entry) => entry.code === wizard.form.data.country);
  const willReprice = Boolean(selected) && selected?.currency !== currentCurrency && repriceableCount > 0;

  const handleContinue = () => {
    if (willReprice) {
      setConfirmOpen(true);
    } else {
      wizard.onPrimary();
    }
  };

  const handleConfirmOpenChange = (open: boolean) => {
    setConfirmOpen(open);
    // Dismiss = keep the current currency: revert the picker to it.
    if (!open) {
      wizard.form.setData("country", country ?? "");
    }
  };

  const handleConfirm = () => {
    setConfirmOpen(false);
    wizard.onPrimary();
  };

  return {
    form: wizard.form,
    fieldError: wizard.fieldError,
    currentStage: wizard.currentStage,
    totalStages: wizard.totalStages,
    onBack: wizard.onBack,
    primaryPending: wizard.primaryPending,
    selected,
    confirmOpen,
    handleContinue,
    handleConfirm,
    handleConfirmOpenChange,
  };
}
